import type { ContentHash, ContentRef } from "@cogo/publisher-core";

/**
 * The publication operations an application authorization can name. Kept local so the immutable
 * contract package never depends on the publication control package that issues the authorization.
 */
export type ApplicationAuthorizationConsumptionOperation = "publish" | "rollback";

export interface ApplicationAuthorizationConsumptionRecord {
  schemaVersion: "cogo.application-authorization-consumption.v1";
  authorizationHash: ContentHash;
  authorizationSignerRef: ContentRef;
  authorizationSignerKeyHash: ContentHash;
  authorizationTrustDomainHash: ContentHash;
  nonceHash: ContentHash;
  publisherCandidateHash: ContentHash;
  environmentHash: ContentHash;
  operation: ApplicationAuthorizationConsumptionOperation;
  consumerRef: ContentRef;
  consumedAt: string;
  contentHash: ContentHash;
}

export type ApplicationAuthorizationConsumptionInput = Omit<
  ApplicationAuthorizationConsumptionRecord,
  "schemaVersion" | "contentHash"
>;

/**
 * Nonce uniqueness is global to the signer trust domain. The store exposes no per-series,
 * per-profile, per-application, per-tenant, or per-key-version partition, so a caller cannot
 * create a second root in which the same nonce is unconsumed.
 */
export interface DurableApplicationAuthorizationStore {
  consumeBeforeEffect(input: ApplicationAuthorizationConsumptionInput): Promise<{
    record: ApplicationAuthorizationConsumptionRecord;
    recordRef: ContentRef;
  }>;
  readByNonce(input: {
    authorizationTrustDomainHash: ContentHash;
    nonceHash: ContentHash;
  }): Promise<ApplicationAuthorizationConsumptionRecord | "consumed-unknown" | null>;
}

export const APPLICATION_AUTHORIZATION_CONSUMPTION_KEYS = Object.freeze([
  "authorizationHash",
  "authorizationSignerRef",
  "authorizationSignerKeyHash",
  "authorizationTrustDomainHash",
  "nonceHash",
  "publisherCandidateHash",
  "environmentHash",
  "operation",
  "consumerRef",
  "consumedAt",
] as const);

export function applicationAuthorizationConsumptionRecordRef(
  record: ApplicationAuthorizationConsumptionRecord,
): ContentRef {
  return {
    ref: `cogo-content:application-authorization-consumption:${record.contentHash}`,
    contentHash: record.contentHash,
  };
}
